import type { FC } from 'react';

import cn from 'classnames';

import { CommunityChannelIcon } from '../ui/CommunityChannelIcon';

import type { ComposerQuotedPost } from './CommunityPostComposer.type';

const formatRelativeTime = (isoDate: string): string => {
  const seconds = Math.floor((Date.now() - new Date(isoDate).getTime()) / 1000);
  if (Number.isNaN(seconds)) return '';
  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

interface QuotedPostPreviewProps {
  post: ComposerQuotedPost;
  className?: string;
}

export const QuotedPostPreview: FC<QuotedPostPreviewProps> = ({ post, className }) => {
  const title = post.title?.trim() ?? '';
  const timeLabel = post.created_at_label ?? formatRelativeTime(post.created_at);
  const authorName = post.author.name ?? '';

  return (
    <div className={cn('rounded-2xl border border-black/10 p-4', className)}>
      <div className="flex items-center gap-2 min-w-0 text-[15px] leading-5 tracking-[-0.15px] text-black/50">
        {post.author.avatar_url ? (
          <img src={post.author.avatar_url} alt={authorName} className="size-8 shrink-0 rounded-full object-cover" />
        ) : (
          <div className="size-8 shrink-0 rounded-full bg-gray-200" />
        )}
        <span className="font-semibold text-black truncate">{authorName}</span>
        {timeLabel && (
          <>
            <span>&bull;</span>
            <span className="whitespace-nowrap">{timeLabel}</span>
          </>
        )}
        {post.space?.name && (
          <>
            <span>&bull;</span>
            <CommunityChannelIcon slug={post.space.slug} emoji={post.space.emoji} className="size-4 shrink-0" />
            <span className="truncate">{post.space.name}</span>
          </>
        )}
      </div>

      {title && (
        <p className="mt-3 text-xl font-semibold leading-6 tracking-[-0.4px] text-black line-clamp-3">{title}</p>
      )}
    </div>
  );
};
